import { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useRouterActions } from 'react-resource-router';

import Heading from '@atlaskit/heading';
import { Box, Flex, Inline, Text, xcss } from '@atlaskit/primitives';
import Select, { type OptionType } from '@atlaskit/select';
import Tag from '@atlaskit/tag';
import Tooltip from '@atlaskit/tooltip';

import { MergeQueue } from 'src/components/pipelines/models/MergeQueue';
import { useParams } from 'src/router/hooks';

import { messages } from '../merge-queues.i18n';
import {
  useFocusedMergeQueue,
  useIsEmpty,
  useIsLoading,
  useMergeQueues,
} from '../provider';

type MergeQueuePickerOptionType = Omit<OptionType, 'label'> & {
  label: React.ReactNode;
};

type RouterParams = {
  repositoryOwner: string;
  repositorySlug: string;
  mergeQueueUuid?: string;
};

function mergeQueueConfigPath(
  repositoryOwner: string,
  repositorySlug: string,
  mergeQueueUuid: string,
  isRenderedInSettings: boolean
): string {
  const segment = isRenderedInSettings ? 'admin/merge-queues' : 'merge-queues';
  return `/${repositoryOwner}/${repositorySlug}/${segment}/configs/${mergeQueueUuid}`;
}

const mergeQueueNameStyles = xcss({ maxWidth: '150px' });
const mergeQueuePickerStyles = xcss({ width: '280px' });

function mergeQueueToOption(
  mergeQueue: MergeQueue
): MergeQueuePickerOptionType {
  const branchName = mergeQueue.branch?.name ?? '';
  const label = (
    <Tooltip content={`${mergeQueue.name} (${branchName})`} position="right">
      <Inline alignBlock="center" space="space.075">
        <Box xcss={mergeQueueNameStyles}>
          <Text maxLines={1}>{mergeQueue.name}</Text>
        </Box>
        {branchName && (
          <Tag isRemovable={false} maxWidth={100} text={branchName} />
        )}
      </Inline>
    </Tooltip>
  );
  return { label, value: mergeQueue.uuid };
}

export const MergeQueuePicker = ({
  isRenderedInSettings = false,
}: {
  isRenderedInSettings?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const [{ isEmpty }] = useIsEmpty();
  const [{ isLoading }] = useIsLoading();
  const [{ mergeQueues }] = useMergeQueues();
  const [{ focusedMergeQueue }] = useFocusedMergeQueue();
  const {
    repositoryOwner,
    repositorySlug,
    mergeQueueUuid: selectedMergeQueueUuid,
  } = useParams<RouterParams>();
  const { push, replace } = useRouterActions();

  const options = useMemo(
    () => mergeQueues.map(mergeQueueToOption),
    [mergeQueues]
  );
  const firstUuid = mergeQueues[0]?.uuid;

  const uuidInList =
    Boolean(selectedMergeQueueUuid) &&
    mergeQueues.some(mq => mq.uuid === selectedMergeQueueUuid);
  const uuidMatchesFocused =
    Boolean(selectedMergeQueueUuid) &&
    focusedMergeQueue?.uuid === selectedMergeQueueUuid;
  const paramValid = uuidInList || uuidMatchesFocused;

  useEffect(() => {
    if (!repositoryOwner || !repositorySlug || !firstUuid) return;
    if (paramValid) return;
    // URL may reference a queue not yet in the list (e.g. right after create while
    // mergeQueues is refreshing); avoid clobbering with first queue until load settles.
    if (selectedMergeQueueUuid && isLoading) return;

    replace(
      mergeQueueConfigPath(
        repositoryOwner,
        repositorySlug,
        firstUuid,
        isRenderedInSettings
      )
    );
  }, [
    firstUuid,
    isLoading,
    isRenderedInSettings,
    mergeQueues,
    paramValid,
    repositoryOwner,
    repositorySlug,
    selectedMergeQueueUuid,
    replace,
  ]);

  const selectedOption =
    options.find(option => option.value === selectedMergeQueueUuid) ??
    options[0] ??
    null;

  const toConfigPath = (uuid: string) =>
    mergeQueueConfigPath(
      repositoryOwner,
      repositorySlug,
      uuid,
      isRenderedInSettings
    );

  if (isEmpty) return null;

  return (
    <Flex alignItems="center" gap="space.100">
      <Heading size="small">{formatMessage(messages.mergeQueue)}:</Heading>
      <Box xcss={mergeQueuePickerStyles}>
        <Select<MergeQueuePickerOptionType>
          isLoading={isLoading}
          isSearchable={false}
          options={options}
          testId="merge-queue-picker"
          value={selectedOption}
          onChange={option => {
            const value = option?.value;
            if (typeof value === 'string') {
              push(toConfigPath(value));
            }
          }}
        />
      </Box>
    </Flex>
  );
};
